/**
 * useDetailPage Hook
 * 
 * Centralized hook for detail pages (user/group detail)
 * Handles:
 * - Fetching data by reference
 * - Update mutations
 * - Modal management
 * - Error handling and toasts
 * - Navigation
 * 
 * @example
 * const {
 *   data: user,
 *   isLoading,
 *   isError,
 *   error,
 *   refetch,
 *   isEditModalOpen,
 *   setIsEditModalOpen,
 *   handleBack,
 *   updateMutation,
 * } = useDetailPage({
 *   reference,
 *   service: userService,
 *   queryKey: 'user',
 *   navigationPath: '/users',
 *   successMessages: {
 *     update: { title: 'Updated!', message: 'Changes saved' },
 *   },
 * });
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from './useToast';

export interface UseDetailPageConfig<T = any> {
  // Data fetching
  reference?: string;
  service: {
    getByReference: (ref: string) => Promise<T>;
    update: (id: string, data: any) => Promise<T>;
  };
  queryKey: string;

  // Navigation
  navigationPath: string;

  // Messages
  successMessages?: {
    update?: { title: string; message?: string };
  };
  errorMessages?: {
    fetch?: string;
    update?: string;
  };

  // Callbacks
  onUpdateSuccess?: (data: T) => void;
  onUpdateError?: (error: Error) => void;
}

export interface UseDetailPageReturn<T = any> {
  // Data
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;

  // Modal
  isEditModalOpen: boolean;
  setIsEditModalOpen: (open: boolean) => void;

  // Mutations
  updateMutation: {
    mutate: (data: any) => void;
    mutateAsync: (data: any) => Promise<T>;
    isPending: boolean;
    isError: boolean;
    error: Error | null;
  };

  // Handlers
  handleBack: () => void;
  handleEdit: () => void;
  handleCancelEdit: () => void;
  handleRefresh: () => void;
}

/**
 * Hook for detail pages
 * Centralizes fetch, mutations, and modal logic
 */
export const useDetailPage = <T extends { id: string } = any>(
  config: UseDetailPageConfig<T>
): UseDetailPageReturn<T> => {
  const {
    reference,
    service,
    queryKey,
    navigationPath,
    successMessages = {},
    errorMessages = {},
    onUpdateSuccess,
    onUpdateError,
  } = config;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  /**
   * Fetch data by reference
   */
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [queryKey, reference],
    queryFn: () => service.getByReference(reference!),
    enabled: !!reference,
    retry: 1,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  /**
   * Update mutation
   */
  const updateMutation = useMutation({
    mutationFn: (updateData: any) => {
      // Use 'ref' if available (for agents), otherwise use 'id'
      const identifier = (data as any)?.ref || data!.id;
      return service.update(identifier, updateData);
    },
    onSuccess: (updatedData) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: [queryKey, reference] });
      queryClient.invalidateQueries({ queryKey: [queryKey] });

      // Close modal
      setIsEditModalOpen(false);

      // Show success toast
      if (successMessages.update) {
        toast.success(
          successMessages.update.title,
          successMessages.update.message
        );
      }

      // Call callback
      onUpdateSuccess?.(updatedData);
    },
    onError: (err: Error) => {
      const errorMsg =
        errorMessages.update || 'Erro ao atualizar. Tente novamente.';
      toast.error(errorMsg, err.message);
      onUpdateError?.(err);
    },
  });

  /**
   * Navigate back
   */
  const handleBack = useCallback(() => {
    navigate(navigationPath);
  }, [navigate, navigationPath]);

  /**
   * Open edit modal
   */
  const handleEdit = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  /**
   * Close edit modal
   */
  const handleCancelEdit = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  /**
   * Refresh data
   */
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    // Data
    data,
    isLoading,
    isError,
    error: error as Error | null,
    refetch: handleRefresh,

    // Modal
    isEditModalOpen,
    setIsEditModalOpen,

    // Mutations
    updateMutation: {
      mutate: updateMutation.mutate,
      mutateAsync: updateMutation.mutateAsync,
      isPending: updateMutation.isPending,
      isError: updateMutation.isError,
      error: updateMutation.error as Error | null,
    },

    // Handlers
    handleBack,
    handleEdit,
    handleCancelEdit,
    handleRefresh,
  };
};
